/**
 * UnknownViewer — fallback file viewer for files that don't have a
 * dedicated renderer (US-08 / Task 9).
 *
 * Selects the right inline rendering for a typed preview payload from
 * `/api/preview`:
 *
 *   - `unknown-text`      → `<pre>` text viewer with monospace styling.
 *   - `unknown-image`     → `<img src="data:…">` inline image.
 *   - `unknown-pdf`       → `<embed type="application/pdf">` viewer.
 *   - `unknown-svg`       → sanitized SVG inlined via
 *                           `dangerouslySetInnerHTML`. The server has
 *                           already stripped `<script>`, `on*` handlers,
 *                           `javascript:` URLs, and `<foreignObject>`
 *                           (see `services/sniff.ts#sanitizeSvg`).
 *   - `unknown-audio`     → native `<audio controls>` streaming from the
 *                           raw file endpoint (FR-6.3).
 *   - `unknown-video`     → native `<video controls>` streaming from the
 *                           raw file endpoint (FR-6.3).
 *   - `unknown-binary`    → metadata summary (`{size, mime, sha256}`)
 *                           plus a `<pre>` hex preview and download link.
 *   - `unknown-oversized` → metadata only + download link. No body.
 *
 * The viewer is pure-presentational; the routing decision is made in
 * `Shell.tsx`'s ContentPane. Pure-helper exports at the bottom of the file
 * allow unit tests to verify selection logic without rendering React.
 */

import { type CSSProperties } from 'react';

/* ── Payload types (mirrors apps/server/src/services/sniff.ts) ────────── */

export interface UnknownTextPayload {
  readonly kind: 'unknown-text';
  readonly path: string;
  readonly mime: string;
  readonly content: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface UnknownImagePayload {
  readonly kind: 'unknown-image';
  readonly path: string;
  readonly mime: string;
  readonly dataBase64: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface UnknownPdfPayload {
  readonly kind: 'unknown-pdf';
  readonly path: string;
  readonly mime: 'application/pdf';
  readonly downloadUrl: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface UnknownSvgPayload {
  readonly kind: 'unknown-svg';
  readonly path: string;
  readonly mime: 'image/svg+xml';
  readonly svg: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface UnknownAudioPayload {
  readonly kind: 'unknown-audio';
  readonly path: string;
  readonly mime: string;
  readonly streamUrl: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface UnknownVideoPayload {
  readonly kind: 'unknown-video';
  readonly path: string;
  readonly mime: string;
  readonly streamUrl: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
}

export interface UnknownBinaryPayload {
  readonly kind: 'unknown-binary';
  readonly path: string;
  readonly mime: string;
  readonly sha256: string;
  readonly mtimeMs: number;
  readonly size: number;
  readonly hexPreview: string;
  readonly downloadUrl: string;
}

export interface UnknownOversizedPayload {
  readonly kind: 'unknown-oversized';
  readonly path: string;
  readonly mime: string;
  readonly sha256Head: string;
  readonly mtimeMs: number;
  readonly size: number;
  readonly downloadUrl: string;
  readonly maxBytes: number;
}

export type UnknownPreviewPayload =
  | UnknownTextPayload
  | UnknownImagePayload
  | UnknownPdfPayload
  | UnknownSvgPayload
  | UnknownAudioPayload
  | UnknownVideoPayload
  | UnknownBinaryPayload
  | UnknownOversizedPayload;

/* ── Pure helpers ─────────────────────────────────────────────────────── */

/** Format a byte count as a human-readable string ("1.2 MB"). */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return `${n}`;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
}

/** Short SHA-256 for the status footer. */
export function shortSha(sha: string): string {
  if (sha.length <= 14) return sha;
  return `${sha.slice(0, 7)}…${sha.slice(-4)}`;
}

/* ── Shared styles ────────────────────────────────────────────────────── */

const containerStyle: CSSProperties = {
  padding: '12px 16px',
  overflow: 'auto',
  height: '100%',
  fontFamily: 'var(--font-interface, system-ui, sans-serif)',
  color: 'var(--text-normal, #dcddde)',
};

const monoStyle: CSSProperties = {
  whiteSpace: 'pre',
  fontFamily: 'var(--font-monospace, ui-monospace, monospace)',
  fontSize: 12,
  background: 'var(--background-secondary, #181818)',
  padding: '8px 10px',
  borderRadius: 4,
  overflow: 'auto',
  color: 'var(--text-normal, #dcddde)',
};

const footerStyle: CSSProperties = {
  marginTop: 12,
  fontSize: 11,
  color: 'var(--text-muted, #a1a1aa)',
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
};

const noticeStyle: CSSProperties = {
  fontSize: 13,
  background: 'var(--background-modifier-form-field, rgba(255,255,255,0.05))',
  padding: '8px 10px',
  borderRadius: 4,
  marginBottom: 12,
};

/* ── Sub-viewers ──────────────────────────────────────────────────────── */

function MetaFooter(props: {
  readonly path: string;
  readonly mime: string;
  readonly size: number;
  readonly sha?: string;
}): JSX.Element {
  return (
    <footer style={footerStyle} data-testid="unknown-meta">
      <span>{props.path}</span>
      <span>·</span>
      <span>{props.mime}</span>
      <span>·</span>
      <span>{formatBytes(props.size)}</span>
      {props.sha ? (
        <>
          <span>·</span>
          <span title={props.sha}>sha256:{shortSha(props.sha)}</span>
        </>
      ) : null}
    </footer>
  );
}

function TextSubViewer(props: { readonly payload: UnknownTextPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-text"
      data-testid="unknown-text-viewer"
      data-kind="unknown-text"
      style={containerStyle}
    >
      <pre style={{ ...monoStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {payload.content}
      </pre>
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function ImageSubViewer(props: { readonly payload: UnknownImagePayload }): JSX.Element {
  const { payload } = props;
  const src = `data:${payload.mime};base64,${payload.dataBase64}`;
  return (
    <article
      className="ow-unknown-image"
      data-testid="unknown-image-viewer"
      data-kind="unknown-image"
      style={containerStyle}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--background-secondary, #181818)',
          padding: 16,
          borderRadius: 4,
        }}
      >
        <img
          src={src}
          alt={payload.path}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
        />
      </div>
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function PdfSubViewer(props: { readonly payload: UnknownPdfPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-pdf"
      data-testid="unknown-pdf-viewer"
      data-kind="unknown-pdf"
      style={containerStyle}
    >
      <embed
        src={payload.downloadUrl}
        type="application/pdf"
        style={{ width: '100%', height: '70vh', border: 0, background: '#fff' }}
        data-testid="unknown-pdf-embed"
      />
      <p style={{ marginTop: 8, fontSize: 12 }}>
        <a href={payload.downloadUrl} download style={{ color: 'var(--text-accent, #7f6df2)' }}>
          Download PDF
        </a>
      </p>
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function SvgSubViewer(props: { readonly payload: UnknownSvgPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-svg"
      data-testid="unknown-svg-viewer"
      data-kind="unknown-svg"
      style={containerStyle}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--background-secondary, #181818)',
          padding: 16,
          borderRadius: 4,
          minHeight: 200,
        }}
        data-testid="unknown-svg-host"
        // The server has already sanitized the SVG (script/onload/foreignObject/
        // javascript: stripped — see services/sniff.ts#sanitizeSvg). Inlining
        // it lets the browser render vector graphics with CSS-driven sizing
        // without an extra HTTP round-trip.
        dangerouslySetInnerHTML={{ __html: payload.svg }}
      />
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function AudioSubViewer(props: { readonly payload: UnknownAudioPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-audio"
      data-testid="unknown-audio-viewer"
      data-kind="unknown-audio"
      style={containerStyle}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--background-secondary, #181818)',
          padding: 16,
          borderRadius: 4,
        }}
      >
        {/* Native browser audio player (FR-6.3), streaming from the raw
            file endpoint — the body is never inlined into the payload. */}
        <audio
          controls
          preload="metadata"
          src={payload.streamUrl}
          style={{ width: '100%' }}
          data-testid="unknown-audio-player"
        >
          Your browser does not support the audio element.{' '}
          <a href={payload.streamUrl} download>
            Download
          </a>
        </audio>
      </div>
      <p style={{ marginTop: 8, fontSize: 12 }}>
        <a href={payload.streamUrl} download style={{ color: 'var(--text-accent, #7f6df2)' }}>
          Download audio
        </a>
      </p>
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function VideoSubViewer(props: { readonly payload: UnknownVideoPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-video"
      data-testid="unknown-video-viewer"
      data-kind="unknown-video"
      style={containerStyle}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--background-secondary, #181818)',
          padding: 16,
          borderRadius: 4,
        }}
      >
        {/* Native browser video player (FR-6.3), streaming from the raw
            file endpoint. */}
        <video
          controls
          preload="metadata"
          src={payload.streamUrl}
          style={{ maxWidth: '100%', maxHeight: '70vh' }}
          data-testid="unknown-video-player"
        >
          Your browser does not support the video element.{' '}
          <a href={payload.streamUrl} download>
            Download
          </a>
        </video>
      </div>
      <p style={{ marginTop: 8, fontSize: 12 }}>
        <a href={payload.streamUrl} download style={{ color: 'var(--text-accent, #7f6df2)' }}>
          Download video
        </a>
      </p>
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function BinarySubViewer(props: { readonly payload: UnknownBinaryPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-binary"
      data-testid="unknown-binary-viewer"
      data-kind="unknown-binary"
      style={containerStyle}
    >
      <p style={noticeStyle} data-testid="unknown-binary-notice">
        Binary file. Showing the first {Math.min(256, payload.size)} bytes as a hex preview.{' '}
        <a href={payload.downloadUrl} download style={{ color: 'var(--text-accent, #7f6df2)' }}>
          Download
        </a>
        .
      </p>
      <pre style={monoStyle} data-testid="unknown-binary-hex">
        {payload.hexPreview}
      </pre>
      <MetaFooter
        path={payload.path}
        mime={payload.mime}
        size={payload.size}
        sha={payload.sha256}
      />
    </article>
  );
}

function OversizedSubViewer(props: { readonly payload: UnknownOversizedPayload }): JSX.Element {
  const { payload } = props;
  return (
    <article
      className="ow-unknown-oversized"
      data-testid="unknown-oversized-viewer"
      data-kind="unknown-oversized"
      style={containerStyle}
    >
      <p style={noticeStyle} data-testid="unknown-oversized-notice">
        File too large to preview inline ({formatBytes(payload.size)} &gt;{' '}
        {formatBytes(payload.maxBytes)}). Showing metadata only.{' '}
        <a href={payload.downloadUrl} download style={{ color: 'var(--text-accent, #7f6df2)' }}>
          Download
        </a>
        .
      </p>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          gap: '4px 12px',
          fontSize: 13,
        }}
      >
        <dt style={{ color: 'var(--text-muted, #a1a1aa)' }}>Path</dt>
        <dd style={{ margin: 0 }}>{payload.path}</dd>
        <dt style={{ color: 'var(--text-muted, #a1a1aa)' }}>Size</dt>
        <dd style={{ margin: 0 }}>
          {formatBytes(payload.size)} ({payload.size.toLocaleString()} bytes)
        </dd>
        <dt style={{ color: 'var(--text-muted, #a1a1aa)' }}>MIME (sniffed)</dt>
        <dd style={{ margin: 0 }}>{payload.mime}</dd>
        <dt style={{ color: 'var(--text-muted, #a1a1aa)' }}>SHA-256 (head)</dt>
        <dd
          style={{
            margin: 0,
            fontFamily: 'var(--font-monospace, ui-monospace, monospace)',
            fontSize: 12,
            wordBreak: 'break-all',
          }}
        >
          {payload.sha256Head}
        </dd>
        <dt style={{ color: 'var(--text-muted, #a1a1aa)' }}>Modified</dt>
        <dd style={{ margin: 0 }}>{new Date(payload.mtimeMs).toISOString()}</dd>
      </dl>
    </article>
  );
}

/* ── Public component ─────────────────────────────────────────────────── */

export interface UnknownViewerProps {
  readonly payload: UnknownPreviewPayload;
}

export function UnknownViewer(props: UnknownViewerProps): JSX.Element {
  const { payload } = props;
  switch (payload.kind) {
    case 'unknown-text':
      return <TextSubViewer payload={payload} />;
    case 'unknown-image':
      return <ImageSubViewer payload={payload} />;
    case 'unknown-pdf':
      return <PdfSubViewer payload={payload} />;
    case 'unknown-svg':
      return <SvgSubViewer payload={payload} />;
    case 'unknown-audio':
      return <AudioSubViewer payload={payload} />;
    case 'unknown-video':
      return <VideoSubViewer payload={payload} />;
    case 'unknown-binary':
      return <BinarySubViewer payload={payload} />;
    case 'unknown-oversized':
      return <OversizedSubViewer payload={payload} />;
    default: {
      const _exhaustive: never = payload;
      return _exhaustive;
    }
  }
}

export default UnknownViewer;
